import { SubAPI } from "./SubAPI";
import {Cents} from "nate-react-api-helpers";
import {Paginated} from "./Users";

export class ProjectSov extends SubAPI {
    getAll(input: {
        project: number;
    }) {
        return this.fetcher.get<SovHeadings>("/api/project-sov", input)
    }

    setMainContractProof(input: {
        project: number;
        file: File | null;
    }) {
        const obj: any = {
            info: JSON.stringify({
                project: input.project,
            }),
        };

        if (input.file) {
            obj.file = input.file;
        }

        return this.fetcher.postFormData<SovHeading>("/api/project-sov/set-main-contract-proof", obj)
    }

    upsertHeading(input: Partial<SovHeading>) {
        return this.fetcher.post<SovHeading>("/api/project-sov/heading", input)
    }

    upsertHeadingItem(input: Partial<SovHeadingItem>) {
        return this.fetcher.post<SovHeadingItem>("/api/project-sov/heading/item", input)
    }

    lockHeadings(input: {
        project: number;
        locked: boolean;
    }) {
        return this.fetcher.post("/api/project-sov/lock-headings", input)
    }

    setPriceLink(props: {
        heading: number;
        prices: number[];
        misc: number[]
        riders: number[];
    }) {
        return this.fetcher.post("/api/project-sov/heading/set-price-link", props)
    }

    upsertChange(input: Partial<SovChange>) {
        return this.fetcher.post<SovChange>("/api/project-sov/change", input)
    }

    upsertChangeTask(input: Partial<SovChange>) {
        return this.fetcher.post<{task: string}>("/api/project-sov/change/task", input)
    }

    getChange(props: {
        changesetId: number;
    }) {
        return this.fetcher.get<SovChange>("/api/project-sov/change", props)
    }

    listChanges(props: {
        project: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<SovChange>>("/api/project-sov/changes", props)
    }

    uploadChangeFile(props: {
        change?: number;
        changeset?: number;
        file: File;
        tag?: string;
    }) {
        return this.fetcher.postFormData("/api/project-sov/change/upload-file", {
            file: props.file,
            info: JSON.stringify({
                change: props.change,
                changeset: props.changeset,
                tag: props.tag
            })
        })
    }

    listChangeFiles(props: {
        change?: number;
        changeset?: number;
        offset?: number;
    }) {
        return this.fetcher.get<Paginated<ChangeFile>>("/api/project-sov/change/files", props)
    }

    upsertChangeFile(props: ChangeFile) {
        return this.fetcher.post<ChangeFile>("/api/project-sov/change/file", props)
    }
}

export type SovHeadings = {
    headings: SovHeading[]
    unassignedPrices: SovUnassignedPriceGroup[];
    locked: boolean;
}

// see NoSovForChangeError in core/project/sov/main.go
export const NoSovForChangeError = "User didn't create an SOV for this change";

export type ChangeFile = {
    id: number;
    change: number;
    file: number;
    archived: boolean;
    createdAt: string;
    createdBy: number;
    createdByName: string;
    tag: "level-1-approval" | "level-2-approval" | "customer-summary" | "";
    name: string;
    amountCents: number | null;
}

export function getFileTagName(tag: "level-1-approval" | "level-2-approval" | "" | string) {
    switch(tag) {
        case "level-1-approval": return "Email Approval";
        case "level-2-approval": return "PO / Signed Contract";
        case "customer-summary": return "Customer Summary";
        default: return tag;
    }
}

export type SovUnassignedPriceGroup = {
    productType: string;
    count: number;
}

export type SovHeading = {
    id: number;
    project: number;
    name: string;

    contractValueCents: Cents;
    billedCents: Cents;
    deliveredCents: Cents;

    reportCostCents: Cents;
    reportCostProjectedCents: Cents;

    archived: boolean;
    updatedAt: string;
    createdAt: string;
    updatedBy: number;
    sortOrder: number;

    items: SovHeadingItem[] | null;
}

export type SovHeadingItem = {
    id: number;
    heading: number;
    project: number;

    name: string;
    percent: number;

    contractValueCents: Cents;
    billedCents: Cents;
    deliveredCents: Cents;

    archived: boolean;
    updatedAt: string;
    createdAt: string;
    updatedBy: number;
    sortOrder: number;
}

export type SovChange = {
    id: number;
    project: number;
    refNumber: string;
    description: string;
    status: "pending" | "approved" | "rejected";

    reviewNotes: string;

    contractValueCents: Cents;
    billedCents: Cents;

    reportCostCents: Cents;
    reportCostProjectedCents: Cents;

    freeformChange: number | null;
    changeset: number | null;

    archived: boolean;
    updatedAt: string;
    createdAt: string;
    updatedBy: number;
    sortOrder: number;
}
